"use client";

import { c as _c } from "react/compiler-runtime";
import type { AxiosError } from "axios";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { useEffect, useRef, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { QueryClient, QueryClientProvider, useQueryClient } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import { dashboardRouterEvents, useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { AuthProvider, SessionExpirationModal } from "@/components/AuthProvider";
import { authHadSession } from "@/lib/authHadSession";
import { logoutUrl, urlInstanceSlug } from "@/lib/named-org";
import { AppearanceSettingsSync } from "@/components/AppearanceSettingsSync";
import dynamic from "next/dynamic";
import { GitHubRateLimitError } from "@/components/GitHubRateLimitError";
import { GithubSsoReauthorizationError } from "@/components/GithubSsoReauthorizationError";
import { GlobalErrorSplash } from "@/components/GlobalErrorSplash";
import { SsoBlockedOrgGate } from "@/components/SsoBlockedOrgGate";
import { Toaster } from "@/components/ui/sonner";
import { Dv360FloodlightGlobal } from "@/components/marketing/Dv360FloodlightGlobal";
import { GtmScripts } from "@/components/marketing/GtmScripts";
import { SpeedInsights } from "@vercel/speed-insights/next";
import { TooltipProvider } from "@repo/blacksmith-ui/tooltip";
import { GitHubRateLimitException, GithubSsoReauthorizationRequiredException, invalidateConnectedGithubOrgs } from "@/hooks/useConnectedGithubOrgs";
import { canRecoverFromStaleChunk, recoverFromStaleChunkRender, useStaleChunkRecovery } from "@/hooks/useStaleChunkRecovery";
import { markNavigationRenderError, useNavigationTimingReporting } from "@/hooks/useNavigationTimingReporting";
import { useWebVitalsReporting } from "@/hooks/useWebVitalsReporting";
import { useMcpOAuthResultListener } from "@/hooks/useMcpServers";
import { useCodesmithExtensionHistoryBridge } from "@/hooks/useCodesmithExtensionHistoryBridge";
import { CODESMITH_EXTENSION_AUTH_MESSAGE } from "@/lib/codesmithExtensionEmbed";
import { codesmithSurface } from "@/lib/codesmithSurface";
import { captureAppFirstTouchRdtCidFromUrl, captureAppFirstTouchUtmCampaignFromUrl } from "@/lib/appFirstTouchAttribution";
import { clearCodesmithLayoutCache } from "@/components/sidebar/useCodesmithEnabledCache";
import { clearOauthSuccessAppLoadGtmSessionFlag } from "@/lib/oauthSuccessAppLoadGtm";
import { installGlobalErrorHandlers, reportBoundaryError } from "@/lib/errorReporting";
import { createRepeatEventFilter } from "@/lib/posthog-dedupe";
import { subscribeToAssistantSessionQueryPromiseCleanup } from "@/lib/assistantSessionQueryPromiseCleanup";
import { PerfDebugMount } from "@/components/perf-debug/PerfDebugMount";

// Keeps Echo and pusher-js out of the shared first-load chunk.
const UserOrgsChannelSync = dynamic(() => import("@/components/UserOrgsChannelSync").then(m => m.UserOrgsChannelSync), {
  ssr: false
});
installGlobalErrorHandlers();

// Created per provider instance rather than at module scope: client components
// also render on the server, where a module-level cache would be shared across
// requests.
function createDashboardQueryClient() {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: 1
      }
    }
  });
  subscribeToAssistantSessionQueryPromiseCleanup(queryClient);
  return queryClient;
}
function ErrorPage(t0) {
  const $ = _c(15);
  const {
    error,
    resetErrorBoundary
  } = t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== queryClient) {
    t1 = () => {
      queryClient.clear();
      authHadSession.clear();
      clearOauthSuccessAppLoadGtmSessionFlag();
      clearCodesmithLayoutCache();
      posthog.reset();
      window.location.href = logoutUrl(urlInstanceSlug());
    };
    $[0] = queryClient;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleLogout = t1;
  if (error instanceof GitHubRateLimitException) {
    let t2;
    if ($[2] !== queryClient || $[3] !== resetErrorBoundary) {
      t2 = () => {
        queryClient.invalidateQueries("connectedGithubOrgs");
        resetErrorBoundary();
      };
      $[2] = queryClient;
      $[3] = resetErrorBoundary;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    let t3;
    if ($[5] !== error.rateLimitReset || $[6] !== error.rateLimitResetSeconds || $[7] !== t2) {
      t3 = <GitHubRateLimitError rateLimitReset={error.rateLimitReset} rateLimitResetSeconds={error.rateLimitResetSeconds} onRetry={t2} />;
      $[5] = error.rateLimitReset;
      $[6] = error.rateLimitResetSeconds;
      $[7] = t2;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    return t3;
  }
  if (error instanceof GithubSsoReauthorizationRequiredException) {
    let t2;
    if ($[9] !== queryClient || $[10] !== resetErrorBoundary) {
      t2 = <GithubSsoReauthorizationError onRetry={() => {
        invalidateConnectedGithubOrgs(queryClient);
        resetErrorBoundary();
      }} />;
      $[9] = queryClient;
      $[10] = resetErrorBoundary;
      $[11] = t2;
    } else {
      t2 = $[11];
    }
    return t2;
  }
  const axiosError = error as AxiosError;
  if (axiosError.response?.status === 401) {
    let t2;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <SessionExpirationModal open={true} setOpen={_temp} />;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    return t2;
  }
  if (canRecoverFromStaleChunk(error)) {
    return null;
  }
  reportBoundaryError(error, queryClient);
  let t2;
  if ($[13] !== handleLogout) {
    t2 = <GlobalErrorSplash onLogout={handleLogout} />;
    $[13] = handleLogout;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  return t2;
}
function _temp() {}
function AppRuntime() {
  const $ = _c(3);
  const previousUrl = useRef("");
  useStaleChunkRecovery();
  useWebVitalsReporting();
  useNavigationTimingReporting();
  useMcpOAuthResultListener();
  useCodesmithExtensionHistoryBridge();
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      captureAppFirstTouchUtmCampaignFromUrl();
      captureAppFirstTouchRdtCidFromUrl();
      const handleStart = () => {
        posthog.capture("$pageleave", {
          $current_url: previousUrl.current
        });
      };
      const handleComplete = () => {
        previousUrl.current = window.location.href;
        posthog.capture("$pageview");
      };
      previousUrl.current = window.location.href;
      dashboardRouterEvents.on("routeChangeStart", handleStart);
      dashboardRouterEvents.on("routeChangeComplete", handleComplete);
      return () => {
        dashboardRouterEvents.off("routeChangeStart", handleStart);
        dashboardRouterEvents.off("routeChangeComplete", handleComplete);
      };
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useEffect(_temp3, t2);
  return null;
}
function _temp3() {
  if (codesmithSurface(new URLSearchParams(location.search).get("embed")) !== "extension") {
    return;
  }
  const handleAuthComplete = _temp2;
  window.addEventListener("message", handleAuthComplete);
  if (window.opener) {
    window.opener.postMessage(CODESMITH_EXTENSION_AUTH_MESSAGE, "*");
    window.close();
  }
  return () => window.removeEventListener("message", handleAuthComplete);
}
function _temp2(event) {
  if (event.origin === window.location.origin && event.data === CODESMITH_EXTENSION_AUTH_MESSAGE) {
    window.location.reload();
  }
}
function PostHogInitialization() {
  useEffect(() => {
    if (window.location.pathname === "/canvas-sandbox") return;
    const toolbarJSON = new URLSearchParams(window.location.hash.substring(1)).get("__posthog");
    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY!, {
      api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com",
      loaded: client => {
        if (process.env.NODE_ENV === "development") client.debug();
        if (!toolbarJSON) return;
        try {
          const config = JSON.parse(toolbarJSON);
          if (config?.action === "ph_authorize") client.loadToolbar(config);
        } catch {
          // Malformed toolbar state; skip the PostHog toolbar.
        }
      },
      opt_in_site_apps: process.env.NODE_ENV !== "development",
      advanced_disable_feature_flags: true,
      before_send: createRepeatEventFilter(),
      // Sentry owns session replay (instrumentation-client.ts); a second
      // rrweb recorder would double the DOM serialization cost for nothing.
      disable_session_recording: true,
      // The document never scrolls (inner panes do), so scroll-depth props
      // are always 0, yet the capture-phase window scroll listener reads
      // scrollHeight on every scroll of any element and forces a style recalc.
      disable_scroll_properties: true
    });
  }, []);
  return null;
}
export function AppProviders(t0) {
  const $ = _c(26);
  const {
    children
  } = t0;
  const router = useRouter();
  const [queryClient] = useState(createDashboardQueryClient);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Dv360FloodlightGlobal />;
    t2 = <GtmScripts />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <PostHogInitialization />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== router.asPath) {
    t4 = [router.asPath];
    $[3] = router.asPath;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <AppearanceSettingsSync />;
    t6 = <UserOrgsChannelSync />;
    $[5] = t5;
    $[6] = t6;
  } else {
    t5 = $[5];
    t6 = $[6];
  }
  let t7;
  if ($[7] !== children) {
    t7 = <AuthProvider>{t5}{t6}<SsoBlockedOrgGate>{children}</SsoBlockedOrgGate></AuthProvider>;
    $[7] = children;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t4 || $[10] !== t7) {
    t8 = <ErrorBoundary FallbackComponent={ErrorPage} onError={_temp5} resetKeys={t4}>{t7}</ErrorBoundary>;
    $[9] = t4;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t10;
  let t11;
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Toaster />;
    t10 = <AppRuntime />;
    t11 = <PerfDebugMount />;
    $[12] = t10;
    $[13] = t11;
    $[14] = t9;
  } else {
    t10 = $[12];
    t11 = $[13];
    t9 = $[14];
  }
  let t12;
  if ($[15] !== t8) {
    t12 = <TooltipProvider>{t8}{t9}{t10}{t11}</TooltipProvider>;
    $[15] = t8;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = process.env.NEXT_PUBLIC_ENABLE_RQ_DEVTOOLS !== "false" && <ReactQueryDevtools initialIsOpen={false} />;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== t12) {
    t14 = <PostHogProvider client={posthog}>{t3}{t12}{t13}</PostHogProvider>;
    $[18] = t12;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== queryClient || $[21] !== t14) {
    t15 = <QueryClientProvider client={queryClient}>{t14}</QueryClientProvider>;
    $[20] = queryClient;
    $[21] = t14;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <SpeedInsights />;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  if ($[24] !== t15) {
    t17 = <>{t1}{t2}{t15}{t16}</>;
    $[24] = t15;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  return t17;
}
function _temp5(error) {
  markNavigationRenderError();
  recoverFromStaleChunkRender(error);
}